import Box from "@mui/material/Box";
import { useState } from "react";
import { Outlet, useMatches } from "react-router";
import { PageHeader } from "@/shared/ui/page-header";
import { Sidebar } from "@/shared/ui/sidebar";
import { TopBarFiltersProvider, useTopBarFiltersSlot } from "@/shared/ui/top-bar-filters-context";
import { PageTitleProvider, usePageTitleSlot } from "@/shared/ui/page-title-context";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { canViewNotifications } from "@/features/deployment-selection/model/permissions";
import {
  NotificationCenterProvider,
  NotificationCenterRail,
  useNotificationCenter,
} from "@/features/deployment-dashboard/notification-center";
import { NotificationPopupStack } from "@/features/deployment-dashboard/notification-center/notification-popup-stack";

function ConsoleLayoutContent() {
  const [collapsed, setCollapsed] = useState(false);
  const { selectedDeployment } = useSelectedDeployment();
  const {
    isPanelOpen,
    unresolvedAlertCount,
    togglePanel,
    popupQueue,
    onExpireNotificationPopup,
    onSelectNotificationPopup,
  } = useNotificationCenter();
  const topBarFilters = useTopBarFiltersSlot();
  const pageTitleSlot = usePageTitleSlot();

  const matches = useMatches();
  const handle = matches.at(-1)?.handle as { title?: string } | undefined;
  const pageTitle = handle?.title ?? "";
  const showNotificationBell = canViewNotifications(selectedDeployment);

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <Sidebar collapsed={collapsed} onToggleCollapse={() => setCollapsed((c) => !c)} />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowX: "auto",
          overflowY: "hidden",
          minWidth: 0,
        }}
      >
        <PageHeader
          title={pageTitleSlot ?? pageTitle}
          accessibleTitle={pageTitle}
          filters={topBarFilters}
          showNotificationBell={showNotificationBell}
          notificationCount={unresolvedAlertCount}
          isNotificationsPanelOpen={isPanelOpen}
          onToggleNotificationsPanel={togglePanel}
        />
        <Box
          component="main"
          sx={{ flex: 1, minWidth: 0, overflowX: "hidden", overflowY: "auto", px: 3, py: 2.5 }}
        >
          <Outlet />
        </Box>
        {isPanelOpen && <NotificationCenterRail />}
      </Box>
      {showNotificationBell && (
        <NotificationPopupStack
          queue={popupQueue}
          onExpire={onExpireNotificationPopup}
          onSelect={onSelectNotificationPopup}
        />
      )}
    </Box>
  );
}

export function ConsoleLayout() {
  return (
    <NotificationCenterProvider>
      <TopBarFiltersProvider>
        <PageTitleProvider>
          <ConsoleLayoutContent />
        </PageTitleProvider>
      </TopBarFiltersProvider>
    </NotificationCenterProvider>
  );
}
